import { MonoTypeOperatorFunction, retry, throwError, timer } from 'rxjs';

/**
 * HTTP statuses that mean "the infrastructure hiccuped", not "the server answered":
 * 0 (network drop / timeout / CORS-less failure) and the gateway statuses 502, 503, 504.
 * A request that failed like this very often succeeds a moment later.
 */
export const TRANSIENT_HTTP_STATUSES: readonly number[] = [0, 502, 503, 504];

/** True when the failed response is a transient infrastructure fault worth retrying. */
export function isTransientHttpFault(err: any): boolean {
  return typeof err?.status === 'number' && TRANSIENT_HTTP_STATUSES.includes(err.status);
}

/**
 * Retries a failed request ONLY for a transient infrastructure fault (see
 * `isTransientHttpFault`), waiting `baseDelayMs * attempt` plus up to `jitterMs` between
 * attempts. Every other error (400, 401, 403, 404, 500 ...) is passed straight through
 * without a retry, and so is a transient one once `retries` is used up.
 *
 * Made for the tenant bootstrap call (SAN-1672 / SC-SAAS-FRONTEND-26): a single 504 there
 * took the whole site down for the visitor. Keep `retries` small, since every retry is extra
 * load on a server that is already struggling when this fires.
 */
export function retryTransientHttp<T>(
  retries = 2,
  baseDelayMs = 1500,
  jitterMs = 500
): MonoTypeOperatorFunction<T> {
  return retry<T>({
    count: retries,
    delay: (err, retryCount) =>
      isTransientHttpFault(err)
        ? timer(baseDelayMs * retryCount + Math.floor(Math.random() * (jitterMs + 1)))
        : throwError(() => err),
  });
}
